import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Avatar,
  useTheme,
} from '@mui/material';
import { Grid } from '@mui/material';
import {
  IconArrowLeft,
  IconPlus,
  IconEdit,
  IconTrash,
  IconClock,
  IconPalette,
  IconCheck,
  IconX,
} from '@tabler/icons-react';
import api from '../services/api';

interface Status {
  _id: string;
  name: string;
  description?: string;
  color: string;
  type: 'available' | 'unavailable' | 'custom';
  isActive: boolean;
  allowedTransitions: string[];
  maxDuration?: number;
  requiresNote: boolean;
  icon?: string;
  priority: number;
  dropIncomingCalls: boolean;
  createdAt: string;
  updatedAt: string;
}

const StatusManagement: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [statuses, setStatuses] = useState<Status[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<Status | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    color: '#0ea5e9',
    type: 'custom' as 'available' | 'unavailable' | 'custom',
    isActive: true,
    canReceiveCalls: true,
    isAvailable: true,
    isBreak: false,
    isOffline: false,
    requiresReason: false,
    allowedTransitions: [] as string[],
    maxDuration: 0,
    autoLogoutAfter: 0,
    requiresNote: false,
    icon: '',
    priority: 5,
    dropIncomingCalls: false
  });

  // Fetch statuses from API
  const fetchStatuses = async () => {
    try {
      setLoading(true);
      const response = await api.get('/agent-statuses');

      if (response.data.success) {
        setStatuses(response.data.data);
      } else {
        setError('Statüler getirilemedi');
      }
    } catch (error: any) {
      console.error('Fetch statuses error:', error);
      if (error.response?.data?.code === 'TOKEN_EXPIRED') {
        setError('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError('Statüler getirilirken hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchStatuses();
  }, [navigate]);

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle status creation/update
  const handleSave = async () => {
    try {
      setSaving(true);
      
      const payload = {
        ...formData,
        canReceiveCalls: Boolean(formData.canReceiveCalls),
        isAvailable: Boolean(formData.isAvailable),
        isBreak: Boolean(formData.isBreak),
        isOffline: Boolean(formData.isOffline),
        requiresReason: Boolean(formData.requiresReason),
        maxDuration: formData.maxDuration || 0,
        autoLogoutAfter: formData.autoLogoutAfter || 0,
        allowedTransitions: formData.allowedTransitions.length > 0 ? formData.allowedTransitions : ['available', 'unavailable']
      };

      if (isEditMode && selectedStatus) {
        // Update existing status
        await api.put(`/agent-statuses/${selectedStatus._id}`, payload);
        setSuccess('Statü başarıyla güncellendi');
      } else {
        // Create new status
        await api.post('/agent-statuses', payload);
        setSuccess('Statü başarıyla oluşturuldu');
      }

      await fetchStatuses();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Save status error:', error);
      if (error.response?.data?.code === 'TOKEN_EXPIRED') {
        setError('Oturum süresi dolmuş. Lütfen tekrar giriş yapın.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(error.response?.data?.message || 'Statü kaydedilemedi');
      }
    } finally {
      setSaving(false);
    }
  };

  // Handle status deletion
  const handleDelete = async (statusId: string) => {
    if (window.confirm('Bu statüyü silmek istediğinizden emin misiniz?')) {
      try {
        await api.delete(`/agent-statuses/${statusId}`);
        
        setSuccess('Statü başarıyla silindi');
        await fetchStatuses();
      } catch (error: any) {
        console.error('Delete status error:', error);
        setError('Statü silinemedi');
      }
    }
  };

  // Open dialog for creating new status
  const handleCreateNew = () => {
    setFormData({
      name: '',
      displayName: '',
      description: '',
      color: '#0ea5e9',
      type: 'custom',
      isActive: true,
      canReceiveCalls: true,
      isAvailable: true,
      isBreak: false,
      isOffline: false,
      requiresReason: false,
      allowedTransitions: ['available', 'unavailable'],
      maxDuration: 0,
      autoLogoutAfter: 0,
      requiresNote: false,
      icon: '',
      priority: priority,
      dropIncomingCalls: false
    });
    setSelectedStatus(null);
    setIsEditMode(false);
    setIsDialogOpen(true);
  };

  // Open dialog for editing existing status
  const handleEdit = (status: Status) => {
    setFormData({
      name: status.name,
      displayName: status.name, // Use name as displayName for now
      description: status.description || '',
      color: status.color,
      type: status.type,
      isActive: status.isActive,
      canReceiveCalls: true, // Default values for missing fields
      isAvailable: status.type === 'available',
      isBreak: status.type === 'unavailable',
      isOffline: false,
      requiresReason: false,
      allowedTransitions: status.allowedTransitions,
      maxDuration: status.maxDuration || 0,
      autoLogoutAfter: 0,
      requiresNote: status.requiresNote,
      icon: status.icon || '',
      priority: status.priority,
      dropIncomingCalls: status.dropIncomingCalls
    });
    setSelectedStatus(status);
    setIsEditMode(true);
    setIsDialogOpen(true);
  };

  // Close dialog and reset form
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedStatus(null);
    setIsEditMode(false);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      color: '#0ea5e9',
      type: 'custom',
      isActive: true,
      canReceiveCalls: true,
      isAvailable: true,
      isBreak: false,
      isOffline: false,
      requiresReason: false,
      allowedTransitions: ['available', 'unavailable'],
      maxDuration: 0,
      autoLogoutAfter: 0,
      requiresNote: false,
      icon: '',
      priority: 5,
      dropIncomingCalls: false
    });
  };

  // Get status type color
  const getStatusTypeColor = (type: string) => {
    switch (type) {
      case 'available': return '#10b981';
      case 'unavailable': return '#ef4444';
      case 'custom': return '#6b7280';
      default: return '#6b7280';
    }
  };

  // Priority input field
  const priority = formData.priority || 5;

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, fontFamily: "'Axiforma', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace" }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton onClick={() => navigate('/admin')} sx={{ mr: 2 }}>
          <IconArrowLeft size={24} />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1e3a8a', mb: 0.5 }}>
            Statü Yönetimi
          </Typography>
          <Typography variant="body1" sx={{ color: '#6b7280' }}>
            Agent statülerini oluşturun, düzenleyin ve yönetin
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<IconPlus size={20} />}
          onClick={handleCreateNew}
          sx={{
            bgcolor: '#0ea5e9',
            '&:hover': { bgcolor: '#0284c7' },
            borderRadius: 2,
            px: 3,
            py: 1.5
          }}
        >
          Yeni Statü
        </Button>
      </Box>

      {/* Status Table */}
      <Card sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid #e5e7eb' }}>
        <CardContent sx={{ p: 0 }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Statü</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Tip</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Renk</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Geçişler</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Durum</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151' }}>Öncelik</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#374151', textAlign: 'center' }}>
                    İşlemler
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statuses.map((status) => (
                  <TableRow key={status._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar
                          sx={{
                            bgcolor: status.color,
                            width: 36,
                            height: 36,
                            fontSize: '0.875rem',
                            fontWeight: 600
                          }}
                        >
                          {status.icon || status.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {status.name}
                          </Typography>
                          {status.description && (
                            <Typography variant="body2" sx={{ color: '#6b7280' }}>
                              {status.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.type === 'available' ? 'Kullanılabilir' : 
                               status.type === 'unavailable' ? 'Kullanılamaz' : 'Özel'}
                        sx={{
                          bgcolor: getStatusTypeColor(status.type),
                          color: 'white',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: status.color,
                            border: '2px solid #e5e7eb'
                          }}
                        />
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {status.color}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ color: '#6b7280' }}>
                        {status.allowedTransitions.length} geçiş
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.isActive ? 'Aktif' : 'Pasif'}
                        sx={{
                          bgcolor: status.isActive ? '#dcfce7' : '#fef3c7',
                          color: status.isActive ? '#166534' : '#92400e',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {status.priority}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
                        <Tooltip title="Düzenle">
                          <IconButton
                            onClick={() => handleEdit(status)}
                            sx={{ color: '#6b7280', '&:hover': { color: '#374151' } }}
                          >
                            <IconEdit size={20} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            onClick={() => handleDelete(status._id)}
                            sx={{ color: '#ef4444', '&:hover': { color: '#dc2626' } }}
                          >
                            <IconTrash size={20} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Status Dialog */}
      <Dialog 
        open={isDialogOpen} 
        onClose={handleCloseDialog}
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ pb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {isEditMode ? 'Statü Düzenle' : 'Yeni Statü'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 0 }}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Statü Adı"
                value={formData.name}
                onChange={(e) => {
                  handleInputChange('name', e.target.value);
                  handleInputChange('displayName', e.target.value);
                }}
                required
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Tip</InputLabel>
                <Select
                  value={formData.type}
                  onChange={(e) => handleInputChange('type', e.target.value)}
                  label="Tip"
                >
                  <MenuItem value="available">Kullanılabilir</MenuItem>
                  <MenuItem value="unavailable">Kullanılamaz</MenuItem>
                  <MenuItem value="custom">Özel</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Açıklama"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                multiline
                rows={2}
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Renk"
                type="color"
                value={formData.color}
                onChange={(e) => handleInputChange('color', e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        bgcolor: formData.color,
                        mr: 1
                      }}
                    />
                  )
                }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Öncelik"
                type="number"
                value={formData.priority}
                onChange={(e) => handleInputChange('priority', parseInt(e.target.value) || 5)}
                inputProps={{ min: 1, max: 10 }}
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Maksimum Süre (dakika)"
                type="number"
                value={formData.maxDuration || ''}
                onChange={(e) => handleInputChange('maxDuration', e.target.value ? parseInt(e.target.value) : undefined)}
                variant="outlined"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <Box sx={{ mt: 2 }}>
                <Button
                  variant={formData.isActive ? "contained" : "outlined"}
                  onClick={() => handleInputChange('isActive', !formData.isActive)}
                  startIcon={formData.isActive ? <IconCheck size={20} /> : <IconX size={20} />}
                  sx={{ mr: 2 }}
                >
                  {formData.isActive ? 'Aktif' : 'Pasif'}
                </Button>
                
                <Button
                  variant={formData.requiresNote ? "contained" : "outlined"}
                  onClick={() => handleInputChange('requiresNote', !formData.requiresNote)}
                  color="secondary"
                  startIcon={<IconClock size={20} />}
                >
                  Not Gerekli
                </Button>
                
                <Button
                  variant={formData.dropIncomingCalls ? "contained" : "outlined"}
                  onClick={() => handleInputChange('dropIncomingCalls', !formData.dropIncomingCalls)}
                  color="error"
                  startIcon={<IconX size={20} />}
                >
                  Gelen Aramaları Düşür
                </Button>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button onClick={handleCloseDialog} variant="outlined">
            İptal
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained"
            disabled={!formData.name || saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Kaydediliyor...' : (isEditMode ? 'Güncelle' : 'Oluştur')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar
        open={!!error}
        autoHideDuration={5000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default StatusManagement;
